import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function filterLookupOptions(options, query, getOptionLabel) {
  if (!query || query.length < MIN_SEARCH_LENGTH) return [];
  const q = query.toLowerCase();
  return options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(q));
}

/**
 * Debounced searchable picker for preloaded lookup lists (PO, vendor, etc.).
 * Same debounce / min-length behaviour as InvItemAutocomplete and PoItemAutocomplete.
 */
export default function InvLookupAutocomplete({
  value,
  options = [],
  onChange,
  getOptionId,
  getOptionLabel,
  label,
  placeholder = 'Search…',
  loading = false,
  noMatchText = 'No matches',
  disabled = false,
  sx,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      return;
    }
    if (selectedOption && getOptionId(selectedOption) === value) return;
    const found = options.find((opt) => getOptionId(opt) === value) || null;
    if (found) setSelectedOption(found);
  }, [value, options, selectedOption, getOptionId]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  const filteredOptions = useMemo(() => {
    const inputQuery = inputValue.trim();
    if (inputQuery.length >= MIN_SEARCH_LENGTH && debouncedSearch.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    return filterLookupOptions(options, debouncedSearch, getOptionLabel);
  }, [options, debouncedSearch, inputValue, getOptionLabel]);

  const displayOptions = useMemo(() => {
    if (isSearching || !selectedOption) return filteredOptions;
    const id = getOptionId(selectedOption);
    if (filteredOptions.some((opt) => getOptionId(opt) === id)) return filteredOptions;
    return [selectedOption, ...filteredOptions];
  }, [filteredOptions, selectedOption, isSearching, getOptionId]);

  const resolvedNoOptionsText = (() => {
    if (loading) return 'Loading…';
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    return noMatchText;
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedOption
            && newInput.trim() !== getOptionLabel(selectedOption)
          ) {
            setSelectedOption(null);
            onChange('');
          }
        }
      }}
      onChange={(_, opt) => {
        setSelectedOption(opt);
        onChange(opt ? getOptionId(opt) : '');
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => getOptionId(a) === getOptionId(b)}
      filterOptions={(x) => x}
      noOptionsText={resolvedNoOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
